import { yupResolver } from '@hookform/resolvers/yup/dist/yup.js'
import { Alert, Box, Button, TextField } from '@mui/material'
import type { CardPartner as ContractCardPartner } from '@mynt/backoffice-api-spec/spendcontrols'
import { getAxiosErrorMessage } from 'api/axios/helpers'
import type { AxiosError } from 'axios'
import { CoreMyntModal } from 'components/modals/CoreMyntModal'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import { useCreateCategoryWithRule } from './adapters'
import { buildPastedValue, isValidOrderInput, parseMccs, parseMids } from './formUtils'

type FormValues = {
  name: string
  description: string
  mccsInput: string
  midsInput: string
  order: string
}

const schema = yup.object({
  name: yup.string().trim().required(),
  description: yup.string().trim().required(),
  mccsInput: yup.string().test('valid-mccs', 'Invalid MCCs', (v) => {
    const { mccs, hasInvalid } = parseMccs(v ?? '')
    return mccs.length > 0 && !hasInvalid
  }),
  midsInput: yup
    .string()
    .default('')
    .test('valid-mids', 'Invalid MIDs', (v) => !parseMids(v ?? '').hasInvalid),
  order: yup
    .string()
    .default('')
    .test('valid-order', 'Must be a non-negative integer', (v) => isValidOrderInput(v ?? '')),
})

export const CreateCategoryModal = ({ open, onClose, partner }: { open: boolean; onClose: () => void; partner: string | undefined }) => {
  const { mutate, isLoading, error, reset } = useCreateCategoryWithRule()

  const {
    register,
    handleSubmit,
    reset: resetForm,
    setValue,
    watch,
    formState: { isValid, errors },
  } = useForm<FormValues>({
    defaultValues: { name: '', description: '', mccsInput: '', midsInput: '', order: '' },
    mode: 'onChange',
    resolver: yupResolver(schema),
  })

  const handleListPaste = (field: 'mccsInput' | 'midsInput') => (e: React.ClipboardEvent) => {
    e.preventDefault()
    const newValue = buildPastedValue(e.target as HTMLTextAreaElement, e.clipboardData.getData('text'))
    setValue(field, newValue, { shouldValidate: true, shouldDirty: true })
  }

  const mccsInputValue = watch('mccsInput')
  const { mccs, hasInvalid: hasInvalidMccs } = parseMccs(mccsInputValue)

  const midsInputValue = watch('midsInput')
  const { mids, hasInvalid: hasInvalidMids } = parseMids(midsInputValue)

  const handleClose = () => {
    resetForm()
    reset()
    onClose()
  }

  const onSubmit = handleSubmit((data) => {
    const name = data.name.trim()
    const description = data.description.trim()
    const parsedOrder = data.order.trim() === '' ? undefined : Number(data.order.trim())
    const payload: Parameters<typeof mutate>[0] = {
      name,
      description,
      cardPartner: partner as ContractCardPartner,
      rule: { name, description, mccs, mids },
    }
    if (parsedOrder !== undefined) payload.order = parsedOrder
    mutate(payload, { onSuccess: handleClose })
  })

  return (
    <CoreMyntModal
      wrapperProps={{ style: { minWidth: 550 } }}
      title="Create category"
      onClose={handleClose}
      open={open}
      actions={() => (
        <Button variant="contained" disabled={!isValid || !partner || isLoading} onClick={onSubmit}>
          {isLoading ? 'Creating...' : 'Create'}
        </Button>
      )}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        {error instanceof Error && <Alert severity="error">{getAxiosErrorMessage(error as AxiosError)}</Alert>}
        <TextField label="Name" {...register('name')} required />
        <TextField label="Description" {...register('description')} required />
        <TextField label="Order" type="number" {...register('order')} error={!!errors.order} />
        <TextField
          label="MCCs"
          {...register('mccsInput')}
          onPaste={handleListPaste('mccsInput')}
          required
          multiline
          minRows={3}
          error={hasInvalidMccs}
          helperText={
            hasInvalidMccs ? 'All values must be 4-digit integers, one per line' : `One MCC per line. ${mccs.length} valid MCC(s) entered.`
          }
        />
        <TextField
          label="MIDs"
          {...register('midsInput')}
          onPaste={handleListPaste('midsInput')}
          multiline
          minRows={3}
          error={hasInvalidMids}
          helperText={
            hasInvalidMids ? 'All values must be positive integers, one per line' : `One MID per line. ${mids.length} MID(s) entered.`
          }
        />
      </Box>
    </CoreMyntModal>
  )
}
